import * as React from "react";
import { Theme } from "@mui/material/styles";
import { SxProps } from "@mui/system";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Fade from "@mui/material/Fade";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Typography from "../components/Typography";
import "./ProductValues.css";
const artImage = require("../../images/theart.jpg");
const processImage = require("../../images/theprocess.jpg");
const plantsImage = require("../../images/plants.jpg");
const storyImage = require("../../images/thinker.jpg");

const item: SxProps<Theme> = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  px: 5,
  gap: 5,
};

const slides = [
  {
    image: artImage,
    alt: "suitcase",
    title: "The Art",
    text: (
      <>
        Scan the World is a global project that captures cultural treasures
        through 3D scanning. These digital treasures are shared in the public
        domain, allowing people everywhere to explore, enjoy, and bring a
        little piece of history to life.
      </>
    ),
  },
  {
    image: processImage,
    alt: "clock",
    title: "The Process",
    text: (
      <>
        We use a resin 3D printer and liquid resin to create miniature
        replicas of cultural artifacts. Each statuette is carefully paired
        with an air plant using plant-safe, waterproof adhesive, transforming
        these replicas into unique works of{" "}
        <span className="species">Living Art</span>.
      </>
    ),
  },
  {
    image: plantsImage,
    alt: "graph",
    title: "The Plants",
    text: (
      <>
        Tillandsias, commonly known as air plants, naturally grow on rock
        faces and trees throughout Central and South America. Requiring no
        soil, these unique plants thrive with just light, air, and water. Our
        Tillandsias are sustainably sourced and carefully nurtured for
        several months in our tiny greenhouse before being hand-selected to
        adorn each statuette.
      </>
    ),
  },
  {
    image: storyImage,
    alt: "thinker",
    title: "The Story",
    text: (
      <>
        This adventure began five years ago with a printing mishap: a Venus
        de Milo emerged from the printer without her head! Rather than let an
        imperfect print go to waste, I added a tiny Ionantha air plant where
        her head should have been&mdash;and voil&agrave;! A happy accident
        became the inspiration for our little company.
      </>
    ),
  },
];

const AUTO_ADVANCE_MS = 10000;

function ProductValues() {
  const [index, setIndex] = React.useState(0);
  const [visible, setVisible] = React.useState(true);
  const nextIndexRef = React.useRef<number | null>(null);

  const goToNext = React.useCallback(() => {
    nextIndexRef.current = (index + 1) % slides.length;
    setVisible(false);
  }, [index]);

  const goToPrev = React.useCallback(() => {
    nextIndexRef.current = (index - 1 + slides.length) % slides.length;
    setVisible(false);
  }, [index]);

  const handleFadeExited = () => {
    if (nextIndexRef.current !== null) {
      setIndex(nextIndexRef.current);
      nextIndexRef.current = null;
      setVisible(true);
    }
  };

  React.useEffect(() => {
    const timer = setInterval(goToNext, AUTO_ADVANCE_MS);
    return () => clearInterval(timer);
  }, [goToNext]);

  const slide = slides[index];

  return (
    <Box
      component="section"
      sx={{ display: "flex", overflow: "hidden", bgcolor: "secondary.light" }}
    >
      <Container sx={{ mt: 15, mb: 30, display: "flex", position: "relative" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <IconButton
            onClick={goToPrev}
            aria-label="Previous"
            sx={{ flexShrink: 0 }}
          >
            <ChevronLeftIcon fontSize="large" />
          </IconButton>
          <Fade in={visible} timeout={600} onExited={handleFadeExited}>
            <Box sx={item}>
              <Box
                component="img"
                src={slide.image}
                alt={slide.alt}
                sx={{ height: 220, flexShrink: 0 }}
              />
              <Box>
                <Typography variant="h6" sx={{ my: 5, textAlign: "center" }}>
                  {slide.title}
                </Typography>
                <Typography variant="h5">{slide.text}</Typography>
              </Box>
            </Box>
          </Fade>
          <IconButton
            onClick={goToNext}
            aria-label="Next"
            sx={{ flexShrink: 0 }}
          >
            <ChevronRightIcon fontSize="large" />
          </IconButton>
        </Box>
      </Container>
    </Box>
  );
}

export default ProductValues;
